import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import SalaryForm from '../components/SalaryForm';
import { canWriteCompensationFor } from '../utils/compensationPermissions';

const AddSalary = () => {
  const { hasPermission, user } = useAuth();
  const methods = useForm();
  const { handleSubmit, setValue, watch, formState: { isSubmitting } } = methods;
  const navigate = useNavigate();
  
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [existingSalary, setExistingSalary] = useState(null);

  // Watch for employee changes to auto-populate payment method and check for existing salary
  const selectedEmployeeId = watch('employee_id');
  
  useEffect(() => {
    if (!hasPermission('salaries', 'create')) {
      toastPermissionDenied('You do not have permission to add salary records');
      navigate('/salaries');
      return;
    }
  }, [hasPermission, navigate]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empRes, pmRes, bankRes] = await Promise.all([
          api.get('/api/employees', { params: { limit: 10000 } }), // Fetch all employees for dropdown
          api.get('/api/payment-methods'),
          api.get('/api/banks')
        ]);
        
        setEmployees(empRes.data.data || []);
        setPaymentMethods(pmRes.data.data);
        setBanks(bankRes.data.data);
      } catch (err) {
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, []);

  // Check for existing salary and auto-populate payment method when employee changes
  useEffect(() => {
    if (!selectedEmployeeId) {
      setExistingSalary(null);
      return undefined;
    }
    // Only the latest pick may fill the form: switching employee while the previous
    // employee's lookups are still in flight would otherwise land that employee's
    // payment details (and existing-salary note) on the one now selected.
    let cancelled = false;

    const checkExistingSalary = async () => {
      try {
        // Check if employee already has a salary record
        const salaryRes = await api.get(`/api/salaries`, { params: { employee_id: selectedEmployeeId } });
        if (cancelled) return;
        if (salaryRes.data.data && salaryRes.data.data.length > 0) {
          setExistingSalary(salaryRes.data.data[0]);
        } else {
          setExistingSalary(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to check existing salary:', err);
        setExistingSalary(null);
      }
    };
    
    const autoPopulatePaymentMethod = async () => {
      try {
        const res = await api.get(`/api/employees/${selectedEmployeeId}/payment-methods`);
        if (cancelled) return;
        const methods = res.data.data || [];
        if (methods.length > 0) {
          const pm = paymentMethods.find(pm => pm.id === methods[0].payment_method_id);
          if (pm) {
            setValue('payment_method_id', pm.id, { shouldValidate: true });
            setValue('account_number', methods[0].account_number || '', { shouldValidate: true });
            setValue('account_title', methods[0].account_title || '', { shouldValidate: true });
            if (methods[0].bank_id) {
              setValue('bank_id', methods[0].bank_id, { shouldValidate: true });
            }
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to auto-populate payment method:', err);
      }
    };

    checkExistingSalary();
    autoPopulatePaymentMethod();
    return () => {
      cancelled = true;
    };
  }, [selectedEmployeeId, paymentMethods, setValue]);

  // A POST also rewrites an existing salary, and the server keeps a caller off their own
  // record unless they hold salaries:update_all, so they are not offered themselves.
  const pickableEmployees = employees.filter(
    (emp) => canWriteCompensationFor(hasPermission, 'salaries', user, emp.id)
  );

  const onSubmit = async (data) => {
    try {
      await api.post('/api/salaries', data);
      toast.success('Salary record added successfully!');
      navigate('/salaries');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add salary record');
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <Link
        to="/salaries"
        data-testid="salary-form-cancel"
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Salaries
      </Link>
      <h2 className="text-2xl font-bold mb-6">Add Salary Record</h2>
      {existingSalary && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This employee already has a salary record. Adding a new salary will update their existing record and add this change to the history.
            {existingSalary.amount_usd && <span> Current USD: ${existingSalary.amount_usd}</span>}
            {existingSalary.amount_pkr && <span> Current PKR: ₨{existingSalary.amount_pkr}</span>}
          </p>
        </div>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <SalaryForm 
            employees={pickableEmployees}
            paymentMethods={paymentMethods}
            banks={banks}
            mode="add"
          />
          <button type="submit" disabled={isSubmitting} data-testid="salary-form-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? (existingSalary ? 'Updating...' : 'Adding...') : (existingSalary ? 'Update Salary Record' : 'Add Salary Record')}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddSalary; 